import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Footprints, Bike, Mountain, PersonStanding, Dumbbell, Users, MapPin, Calendar, Coffee, Instagram, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getClubImageUrls } from "@/lib/club-images";

export const Route = createFileRoute("/club/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `Club ${params.slug} — Brulo` },
      { name: "description", content: "Conoce este club y únete a sus próximas salidas." },
      { property: "og:title", content: `Club ${params.slug} — Brulo` },
      { property: "og:description", content: "Conoce este club y únete a sus próximas salidas." },
    ],
  }),
  component: ClubPage,
});

type Club = {
  id: string;
  name: string;
  description: string | null;
  city: string | null;
  estado: string | null;
  type: string;
  member_count_range: string | null;
  instagram: string | null;
  slug: string;
  logo_url: string | null;
  banner_url: string | null;
  accent_color: string | null;
};

type EventRow = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  meeting_point: string;
  tipo_actividad: string | null;
  cafe_id: string | null;
  club_id: string | null;
  cancelado: boolean;
  cafes: { id: string; name: string; location: string | null; city: string | null } | null;
};

type CafeRow = { id: string; name: string; location: string | null; city: string | null };

function typeMeta(type: string | null | undefined) {
  switch (type) {
    case "ciclismo":
    case "ride":
      return { label: "Ciclismo", Icon: Bike, emoji: "🚴" };
    case "trail":
      return { label: "Trail", Icon: Mountain, emoji: "🏔️" };
    case "caminata":
      return { label: "Caminata", Icon: PersonStanding, emoji: "🚶" };
    case "multideporte":
    case "both":
      return { label: "Multideporte", Icon: Dumbbell, emoji: "💪" };
    default:
      return { label: "Running", Icon: Footprints, emoji: "🏃" };
  }
}

function ClubPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [club, setClub] = useState<Club | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [cafes, setCafes] = useState<CafeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Join state
  const [role, setRole] = useState<string | null>(null);
  const [existingRequest, setExistingRequest] = useState<{ status: string } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: c } = await supabase
        .from("clubs")
        .select("id,owner_id,name,description,city,estado,type,member_count_range,instagram,slug,logo_url,banner_url,accent_color")
        .eq("slug", slug)
        .maybeSingle<Club & { owner_id: string }>();
      if (!c) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setClub(c);
      const [logo, banner] = await getClubImageUrls([c.logo_url, c.banner_url]);
      setLogoUrl(logo);
      setBannerUrl(banner);



      const todayStr = new Date().toISOString().slice(0, 10);

      // Fetch only upcoming, non-cancelled events linked to this club.
      const { data: evs } = await supabase
        .from("events")
        .select("id,name,event_date,event_time,meeting_point,tipo_actividad,cafe_id,cancelado,club_id")
        .eq("club_id", c.id)
        .gte("event_date", todayStr)
        .eq("cancelado", false)
        .order("event_date", { ascending: true });
      let evList: EventRow[] = ((evs ?? []) as Array<Omit<EventRow, "cafes">>).map((event) => ({
        ...event,
        cafes: null,
      }));

      // Meeting cafes: union of cafés used in any of this club's events
      const cafeIds = Array.from(
        new Set(evList.map((e) => e.cafe_id).filter((x): x is string => !!x)),
      );
      let cafeList: CafeRow[] = [];
      if (cafeIds.length) {
        const { data: cf } = await supabase
          .from("cafes")
          .select("id,name,location,city")
          .in("id", cafeIds);
        cafeList = (cf ?? []) as CafeRow[];
        const cafeById = new Map(cafeList.map((cafe) => [cafe.id, cafe]));
        evList = evList.map((event) => ({
          ...event,
          cafes: event.cafe_id ? (cafeById.get(event.cafe_id) ?? null) : null,
        }));
      }
      setEvents(evList);
      setCafes(cafeList);
      setLoading(false);
    })();
  }, [slug]);

  // Load role + existing request once auth resolved
  useEffect(() => {
    if (authLoading || !user || !club) return;
    (async () => {
      const { data: prof } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .maybeSingle();
      setRole(prof?.role ?? null);

      const { data: req } = await supabase
        .from("club_join_requests")
        .select("status")
        .eq("club_id", club.id)
        .eq("user_id", user.id)
        .maybeSingle();
      if (req) setExistingRequest(req as any);
    })();
  }, [user, club, authLoading]);

  const onJoinClick = () => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth", search: { next: `/club/${slug}` } as any });
      return;
    }
    if (role && role !== "runner") {
      toast.error("Solo cuentas de atleta pueden solicitar unirse a un club.");
      return;
    }
    setShowForm(true);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !club) return;
    setSubmitting(true);
    const { error } = await supabase
      .from("club_join_requests")
      .insert({ club_id: club.id, user_id: user.id, message: message.trim() || null });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setSubmitted(true);
    setShowForm(false);
    setExistingRequest({ status: "pending" });
  };

  if (loading) {
    return <div className="mx-auto max-w-4xl px-4 py-20 text-muted-foreground">Cargando...</div>;
  }
  if (notFound || !club) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20">
        <h1 className="font-display text-3xl">Club no encontrado</h1>
        <Link to="/" className="text-primary hover:underline mt-4 inline-block">Volver al inicio</Link>
      </div>
    );
  }

  const tm = typeMeta(club.type);
  const TypeIcon = tm.Icon;

  const cta = (() => {
    if (submitted || existingRequest?.status === "pending") {
      return { label: "Solicitud enviada ✓", disabled: true };
    }
    if (existingRequest?.status === "approved") {
      return { label: "Ya eres miembro ✓", disabled: true };
    }
    if (existingRequest?.status === "rejected") {
      return { label: "Solicitud no aceptada", disabled: true };
    }
    return { label: "Quiero unirme", disabled: false };
  })();

  const accent = club.accent_color && /^#[0-9a-fA-F]{6}$/.test(club.accent_color) ? club.accent_color : null;
  const accentStyle = accent ? { backgroundColor: accent, borderColor: accent } : undefined;
  const accentTextStyle = accent ? { color: accent } : undefined;
  const accentBorderStyle = accent ? { borderColor: accent, color: accent } : undefined;

  return (
    <div className="pb-16">
      <div className="mx-auto max-w-5xl px-4 md:px-6 pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Inicio
        </Link>
      </div>

      {/* Banner */}
      <div className="relative mt-4 mx-auto max-w-5xl px-4 md:px-6">
        <div
          className="relative aspect-[3/1] sm:aspect-[4/1] rounded-xl overflow-hidden border border-border bg-gradient-to-br from-muted/60 to-muted/20"
          style={accent ? { backgroundColor: `${accent}22` } : undefined}
        >
          {bannerUrl ? (
            <img src={bannerUrl} alt={`Banner de ${club.name}`} className="absolute inset-0 w-full h-full object-cover" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>
        {/* Logo overlapping banner */}
        <div className="absolute left-8 md:left-12 -bottom-12 md:-bottom-14">
          <div
            className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-4 border-background bg-muted shadow-xl"
            style={accent ? { borderColor: "var(--background)", boxShadow: `0 0 0 2px ${accent}` } : undefined}
          >
            {logoUrl ? (
              <img src={logoUrl} alt={`Logo de ${club.name}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl font-display text-muted-foreground">
                {club.name.charAt(0)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 md:px-6 pt-20 md:pt-24 space-y-10">
        {/* Header info */}
        <header className="space-y-5">
          <p className="text-xs tracking-[0.3em] uppercase" style={accentTextStyle ?? { color: "var(--primary)" }}>club</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">{club.name}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-background/60 border"
              style={accentBorderStyle ?? { borderColor: "rgb(var(--primary) / 0.3)", color: "var(--primary)" }}
            >
              <TypeIcon className="w-3.5 h-3.5" /> {tm.label}
            </span>
            {(club.city || club.estado) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {[club.city, club.estado].filter(Boolean).join(", ")}
              </span>
            )}
            {club.member_count_range && (
              <span className="inline-flex items-center gap-1.5">
                <Users className="w-4 h-4" /> {club.member_count_range} miembros
              </span>
            )}
          </div>
          {club.description && (
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed max-w-2xl whitespace-pre-line">
              {club.description}
            </p>
          )}
          {club.instagram && (
            <a
              href={`https://instagram.com/${club.instagram.replace(/^@/, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm hover:underline"
              style={accentTextStyle ?? { color: "var(--primary)" }}
            >
              <Instagram className="w-4 h-4" /> @{club.instagram.replace(/^@/, "")}
            </a>
          )}

          <div className="pt-2">
            <button
              onClick={onJoinClick}
              disabled={cta.disabled}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-primary-foreground font-semibold text-base shadow-lg hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
              style={accentStyle ?? { backgroundColor: "var(--primary)" }}
            >
              {cta.label}
            </button>
            {submitted && (
              <p className="mt-3 text-sm" style={accentTextStyle ?? { color: "var(--primary)" }}>
                Tu solicitud fue enviada. El coach del club revisará tu perfil.
              </p>
            )}
          </div>
        </header>


      {/* Salidas del club: only upcoming, non-cancelled */}
      <section>
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" /> Salidas del club
        </h2>
        {(() => {
          if (events.length === 0) {
            return (
              <p className="text-muted-foreground text-sm">
                Este club no tiene salidas programadas por el momento. ¡Únete y sé el primero en enterarte!
              </p>
            );
          }
          return (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((ev) => {
                const em = typeMeta(ev.tipo_actividad);
                const dateStr = new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("es-MX", {
                  weekday: "short", day: "numeric", month: "short",
                });
                return (
                  <Link
                    key={ev.id}
                    to="/eventos/$eventId"
                    params={{ eventId: ev.id }}
                    className="block rounded-lg border bg-background/40 p-4 hover:bg-background/60 transition border-border hover:border-primary/40"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-xs uppercase tracking-wider text-primary">
                        {em.emoji} {em.label}
                      </p>
                    </div>
                    <h3 className="font-display text-lg leading-snug mb-2">{ev.name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">
                      {dateStr} · {ev.event_time?.slice(0, 5)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1.5">
                      <Coffee className="w-3.5 h-3.5" />
                      {ev.cafes?.name ?? ev.meeting_point}
                    </p>
                  </Link>
                );
              })}
            </div>
          );
        })()}
      </section>

      {/* Meeting cafés */}
      {cafes.length > 0 && (
        <section>
          <h2 className="font-display text-2xl mb-4 flex items-center gap-2">
            <Coffee className="w-5 h-5 text-primary" /> Cafés de encuentro
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {cafes.map((c) => (
              <div key={c.id} className="rounded-lg border border-border bg-background/40 p-4">
                <h3 className="font-display text-lg">{c.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {[c.location, c.city].filter(Boolean).join(" · ")}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Join modal */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => !submitting && setShowForm(false)}
        >
          <form
            onSubmit={onSubmit}
            onClick={(e) => e.stopPropagation()}
            className="bg-background border border-border rounded-lg p-6 max-w-md w-full space-y-4"
          >
            <h3 className="font-display text-xl">Unirme a {club.name}</h3>
            <label className="block text-sm space-y-2">
              <span className="text-muted-foreground">¿Por qué quieres unirte?</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder="Cuéntale al coach un poco sobre ti, tu nivel y por qué te interesa el club..."
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm focus:outline-none focus:border-primary"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                disabled={submitting}
                className="flex-1 px-4 py-2 rounded-md border border-border text-sm font-semibold hover:bg-muted/50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? "Enviando..." : "Enviar solicitud"}
              </button>
            </div>
          </form>
        </div>
      )}
      </div>
    </div>
  );
}
